import { useEffect } from "react";
import { SITE } from "@/data/site";
import { useLang } from "@/lib/i18n";

// Embeds the appropriate GHL form for the current language.
//
// IMPORTANT: GoHighLevel's `form_embed.js` mutates the DOM around the iframe
// (it inserts wrappers / sibling nodes for postMessage-driven resizing).
// When the language toggles and React tried to swap iframes via `key={form.id}`,
// React's reconciliation crashed with:
//   "Failed to execute 'removeChild' on 'Node':
//    The node to be removed is not a child of this node."
//
// Fix: render the iframe with `dangerouslySetInnerHTML` so React treats the
// container as opaque, and `key={lang}` on the wrapper so the whole subtree
// remounts cleanly on every language switch. Re-injecting form_embed.js on
// every language change re-attaches the resize listeners to the new iframe.
export default function GHLForm({ minHeight = 720 }) {
  const { lang } = useLang();
  const form = SITE.ghlForms[lang] || SITE.ghlForms.en;

  useEffect(() => {
    // Remove any prior copy then re-inject so GHL re-scans for the new iframe.
    document
      .querySelectorAll(`script[data-fp-ghl="1"]`)
      .forEach((el) => el.remove());
    const s = document.createElement("script");
    s.src = SITE.ghlFormEmbedScript;
    s.async = true;
    s.dataset.fpGhl = "1";
    document.body.appendChild(s);
  }, [lang]);

  const iframeHTML = `<iframe
    title="${form.name}"
    src="${form.src}"
    id="inline-${form.id}"
    data-layout="{'id':'INLINE'}"
    data-trigger-type="alwaysShow"
    data-trigger-value=""
    data-activation-type="alwaysActivated"
    data-activation-value=""
    data-deactivation-type="neverDeactivate"
    data-deactivation-value=""
    data-form-name="${form.name.replace(/"/g, "&quot;")}"
    data-height="${minHeight}"
    data-layout-iframe-id="inline-${form.id}"
    data-form-id="${form.id}"
    style="width:100%;height:100%;min-height:${minHeight}px;border:none;border-radius:12px"
  ></iframe>`;

  return (
    <div
      key={lang}
      data-testid="ghl-form-wrapper"
      data-form-lang={lang}
      className="w-full bg-white"
      style={{ minHeight }}
      dangerouslySetInnerHTML={{ __html: iframeHTML }}
    />
  );
}
